// utils/dateFormatters.js
import { format, parseISO, differenceInDays, startOfMonth, endOfDay } from 'date-fns';
import logger from '../../../shared/services/logging/logger';

/**
 * Format date to YYYY-MM-DD
 */
export const formatDateYYYYMMDD = (date) => {
  if (!date) return null;
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return format(dateObj, 'yyyy-MM-dd');
  } catch (error) {
    logger.error('Error formatting date', { error: error.message });
    return null;
  }
};

/**
 * Format date for display (MMM dd, yyyy)
 */
export const formatDateDisplay = (date) => {
  if (!date) return '';
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return format(dateObj, 'MMM dd, yyyy');
  } catch (error) {
    logger.error('Error formatting date', { error: error.message });
    return '';
  }
};

/**
 * Format timestamp for display
 */
export const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  try {
    const dateObj = typeof timestamp === 'string' ? parseISO(timestamp) : new Date(timestamp);
    return format(dateObj, 'MMM dd, yyyy HH:mm:ss');
  } catch (error) {
    logger.error('Error formatting timestamp', { error: error.message });
    return '';
  }
};

/**
 * Get default date range (current month)
 */
export const getDefaultDateRange = () => {
  const now = new Date();
  const startDate = startOfMonth(now);
  const endDate = endOfDay(now);
  
  return {
    startDate: formatDateYYYYMMDD(startDate),
    endDate: formatDateYYYYMMDD(endDate)
  };
};

/**
 * Calculate days between two dates
 */
export const calculateDaysBetween = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  try {
    const start = typeof startDate === 'string' ? parseISO(startDate) : startDate;
    const end = typeof endDate === 'string' ? parseISO(endDate) : endDate;
    return differenceInDays(end, start) + 1; // +1 to include both start and end day
  } catch (error) {
    logger.error('Error calculating days between', { error: error.message });
    return 0;
  }
};

/**
 * Calculate days remaining from today to end date
 */
export const calculateDaysRemaining = (endDate) => {
  if (!endDate) return 0;
  try {
    const end = typeof endDate === 'string' ? parseISO(endDate) : endDate;
    const now = new Date();
    const days = differenceInDays(end, now);
    return days < 0 ? 0 : days;
  } catch (error) {
    logger.error('Error calculating days remaining', { error: error.message });
    return 0;
  }
};

/**
 * Format duration (minutes)
 */
export const formatDuration = (minutes) => {
  if (!minutes || minutes === 0) return '0m';
  
  const mins = Math.floor(minutes);
  const secs = Math.round((minutes - mins) * 60);
  
  if (mins === 0) return `${secs}s`;
  if (secs === 0) return `${mins}m`;
  return `${mins}m ${secs}s`;
};

/**
 * Get date range label for display
 */
export const getDateRangeLabel = (startDate, endDate) => {
  if (!startDate || !endDate) return '';
  return `${formatDateDisplay(startDate)} to ${formatDateDisplay(endDate)}`;
};